import { useEffect, useRef } from 'react';

const useStickyScroll = ({ isIE, dynamicScroll, columnName, wrapperMargin }) => {
  const ref = useRef();
  const scrollPosition = useRef(0);
  const ticking = useRef(false);

  const handleScrollUp = ({ sticky, setElementTop, updateElementPosition }) => {
    const stickyRect = sticky.getBoundingClientRect();
    const stickyTopIsOnViewport = stickyRect.top >= wrapperMargin;

    if (stickyTopIsOnViewport) {
      setElementTop(wrapperMargin);
    } else {
      updateElementPosition();
    }
  };

  const handleScrollDown = ({ sticky, setElementTop, updateElementPosition }) => {
    const viewportHeight = window.innerHeight;
    const stickyRect = sticky.getBoundingClientRect();

    // Check if the bottom edge of the sticky element has reached the bottom of the viewport
    const stickyBottomIsOnViewport = stickyRect.bottom <= viewportHeight - wrapperMargin;

    /*
     * Compute the offset that should be applied to the sticky element once its
     * bottom edge reaches the bottom of the viewport. The idea is:
     *   1. When the sticky element is taller than the viewport, we need to move it
     *      upwards so that its bottom edge remains visible (hence a negative offset).
     *   2. When it is shorter (or equal) than the viewport, we never want to move
     *      it downwards past the original top margin. In that case we simply clamp
     *      the value to `wrapperMargin`.
     */
    const rawOffset = stickyRect.height - viewportHeight + wrapperMargin;
    const pixelsToStickyTop = rawOffset > 0 ? -rawOffset : wrapperMargin; // clamp for all viewport heights

    if (stickyBottomIsOnViewport) {
      setElementTop(pixelsToStickyTop);
    } else {
      updateElementPosition();
    }
  };

  const onScroll = (sticky, column) => {
    if (!ticking.current) {
      window.requestAnimationFrame(() => {
        const newScrollPosition = window.scrollY;

        const pixelsToTranslate = Math.abs(column.getBoundingClientRect().top - sticky.getBoundingClientRect().top);

        const updateElementPosition = () => {
          sticky.style.top = '';
          sticky.style.transform = `translateY(${pixelsToTranslate}px)`;
        };

        const setElementTop = pixels => {
          sticky.style.top = `${pixels}px`;
          sticky.style.transform = '';
        };

        if (newScrollPosition > scrollPosition.current) {
          handleScrollDown({ sticky, setElementTop, updateElementPosition });
        } else {
          handleScrollUp({ sticky, setElementTop, updateElementPosition });
        }

        scrollPosition.current = newScrollPosition;
        ticking.current = false;
      });
    }
    ticking.current = true;
  };

  useEffect(() => {
    const sticky = ref.current;
    const column = columnName && document.querySelector(`.ui-pdp-container--column-${columnName}`);
    const compatibleAndEnabled = !isIE && dynamicScroll?.enabled;
    const stickyElementOverflowsViewport = window.innerHeight < sticky?.clientHeight;
    const shouldUseScroll = sticky && column && compatibleAndEnabled && stickyElementOverflowsViewport;
    const scrollHandler = () => onScroll(sticky, column);

    // Esto cubre el caso de la galeria o columnas de poco contenido
    if (compatibleAndEnabled && !stickyElementOverflowsViewport) {
      sticky.style.top = `${wrapperMargin}px`;
    }

    const listenerOptions = { passive: true };

    if (shouldUseScroll) {
      document.addEventListener('scroll', scrollHandler, listenerOptions);
    }

    return () => {
      if (shouldUseScroll) {
        document.removeEventListener('scroll', scrollHandler, listenerOptions);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnName, ref, dynamicScroll, isIE]);

  return ref;
};

export default useStickyScroll;
